"use client";

/**
 * 업로드 전 브라우저에서 사진을 줄인다(INFRA.md B1 — Vercel 4.5MB 본문 한도 대응).
 * 요즘 폰 사진은 원본이 3~8MB라 그대로 보내면 자주 걸린다. 긴 변 2,000px, JPEG
 * 품질을 점점 낮춰가며 목표 용량 이하가 될 때까지 다시 인코딩한다.
 *
 * HEIC/HEIF는 <canvas>가 디코딩을 보장하지 않아(사파리 일부만 지원) 여기서는
 * 건드리지 않고 원본 그대로 둔다 — 크기 검사는 호출부(checkUploadFile)가 별도로 한다.
 */
const MAX_DIMENSION = 2000;
const QUALITY_STEPS = [0.82, 0.7, 0.6, 0.5, 0.4];

const COMPRESSIBLE_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

export function canCompress(file: File): boolean {
  return COMPRESSIBLE_TYPES.has(file.type);
}

export async function compressImageFile(file: File, maxBytes: number): Promise<File> {
  if (!canCompress(file)) return file;

  const bitmap = await createImageBitmap(file).catch(() => null);
  if (!bitmap) return file; // 디코딩 실패 시 원본 그대로(호출부가 크기 검사로 걸러낸다)

  const scale = Math.min(1, MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return file;
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  for (const quality of QUALITY_STEPS) {
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", quality),
    );
    if (!blob) continue;
    if (blob.size <= maxBytes || quality === QUALITY_STEPS[QUALITY_STEPS.length - 1]) {
      const name = file.name.replace(/\.[^.]+$/, "") + ".jpg";
      return new File([blob], name, { type: "image/jpeg" });
    }
  }
  return file;
}
